//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { createSelector } from 'reselect';

import { OrderedInstagramReelsCreativeNotesTypes } from '@instagram-account/post-details/postDetailsUIConstants';
import { IReelsCreativeNote } from '@instagram-account/redux/instagram-reels-post-details/instagramReelsPostDetailsModel';
import { ICuralateStore } from '@models/AppState';
import { isDefined } from '@utils/filterPredicates';

export const getCurrentReelsPostDetails = (state: ICuralateStore) =>
  state.instagram.postDetails.reels.currentReelsPostDetails;

export const getCurrentReelsCustomizations = createSelector(
  getCurrentReelsPostDetails,
  (currentReelsPostDetails) => currentReelsPostDetails.customizations,
);

export const getCurrentReelsAudio = createSelector(
  getCurrentReelsCustomizations,
  (customizations) => customizations.audio,
);

export const getCurrentReelsCaption = createSelector(
  getCurrentReelsCustomizations,
  (customizations) => customizations.caption,
);

export const getCurrentReelsEffects = createSelector(
  getCurrentReelsCustomizations,
  (customizations) => customizations.effects,
);

export const getCurrentReelsLocation = createSelector(
  getCurrentReelsCustomizations,
  (customizations) => customizations.location,
);

export const getCurrentReelsOther = createSelector(
  getCurrentReelsCustomizations,
  (customizations) => customizations.other,
);

export const getCurrentReelsShareToFeed = createSelector(
  getCurrentReelsPostDetails,
  (currentReelsPostDetails) => currentReelsPostDetails.shareToFeed,
);

export const getCurrentReelsStickers = createSelector(
  getCurrentReelsCustomizations,
  (customizations) => customizations.stickers,
);

export const getCurrentReelsTextOverlay = createSelector(
  getCurrentReelsCustomizations,
  (customizations) => customizations.textOverlay,
);

export const getCurrentReelsUserTags = createSelector(
  getCurrentReelsCustomizations,
  (customizations) => customizations.userTags,
);

export const getAddableReelsCreativeNotesTypes = createSelector(
  getCurrentReelsCustomizations,
  (customizations) => {
    return OrderedInstagramReelsCreativeNotesTypes.filter((noteType) => {
      switch (noteType) {
        case 'textOverlay':
          return true;
        case 'stickers':
          return true;
        case 'other':
          return true;
        case 'audio':
          return !isDefined(customizations.audio);
        case 'effects':
          return !isDefined(customizations.effects);
      }
    });
  },
);

export const getCurrentReelsCreativeNotes = createSelector(
  getCurrentReelsCustomizations,
  (customizations) => {
    const creativeNotes: IReelsCreativeNote[] = [];

    OrderedInstagramReelsCreativeNotesTypes.forEach((noteType) => {
      const notes = customizations[noteType];

      if (isDefined(notes)) {
        if (Array.isArray(notes)) {
          notes.forEach((note, index) => {
            creativeNotes.push({
              type: noteType,
              index,
              value: note,
            });
          });
        } else {
          creativeNotes.push({
            type: noteType,
            value: notes,
          });
        }
      }
    });

    return creativeNotes;
  },
);
